import { defineStore } from 'pinia';
import { computed, Ref, ref } from "vue";
import axios from 'axios';

import { endpoint } from "@/remote/utils";
import {
  IssueMarker,
  MaintenanceKind,
  MaintenanceMarker,
  PreemptiveMaintenanceMarker
} from '@/components/MaintenanceMap/types';

export const monthMs = 1000 /*ms*/ * 60 /*s*/ * 60 /*min*/ * 24 /*h*/ * 30 /*d*/;
export const yearMs = monthMs * 12;

export function time_since_last_maintenance(marker: PreemptiveMaintenanceMarker): number {
  const date = Date.parse(marker.date);
  const today = Date.now();
  return today - date;
}

export function compare_markers(a: MaintenanceMarker, b: MaintenanceMarker): number {
  return Date.parse(a.date) - Date.parse(b.date);
}

export function compare_issues(a: IssueMarker, b: IssueMarker) {
  if (a.priority < b.priority) {
    return -1;
  } else if (a.priority > b.priority) {
    return 1;
  } else {
    if (a.date < b.date) {
      return 1;
    } else {
      return 1;
    }
  }
}

const preemptive_colors: Array<{ text: 'Blue' | 'Green' | 'Orange' | 'Red', color: string }> = [
  { text: "Blue", color: "#0373a4" },
  { text: "Green", color: "#00ff00" },
  { text: "Orange", color: "#ff8d3f" },
  { text: "Red", color: "#ff0000" }
] as const;


export function get_preemptive_color(color: string): string {
  const col = preemptive_colors.find(e => e.text === color);
  if (col) {
    return col.color;
  }
  return "#000000";
}

export function maintenance_marker_color(marker: IssueMarker | PreemptiveMaintenanceMarker): string {
  if (marker.kind === MaintenanceKind.issue) {
    if (marker.priority === 1) {
      return get_preemptive_color("Red");
    }
    if (marker.priority === 2) {
      return get_preemptive_color("Orange");
    }
    return get_preemptive_color("Green");
  } else {
    const diff = time_since_last_maintenance(marker);
    if (diff - 6 * monthMs > 0) {
      return get_preemptive_color("Red");
    }
    if (diff - 2 * monthMs > 0) {
      return get_preemptive_color("Orange");
    }
    if (diff + 4 * monthMs > 0) {
      return get_preemptive_color("Green");
    }
    return get_preemptive_color("Blue");
  }
}

type IssueType = {
  id: number,
  park: number,
  name: string,
  kind: MaintenanceKind,
  made_by: number,
  issue_descr: string,
  start_date: string,
  is_solved: boolean,
  title: string,
  priority: number,
  last_curative_date: string,
  how_to_solve_descr: string,
  agency?: AgencyType
}

type ParkType = {
  id: number,
  name: string,
  latitude: string,
  longitude: string,
  agency: number,
  address: string,
  city: string,
  cabinet_position: string | null,
  cabinet_key: boolean | null,
  installation_date: string,
  contract_maintenance_amount: number,
  latest_preventive_maintenance_date: string | null,
  has_contract: boolean,
  contract_type: string | null,
  number_of_cs: number,
  how_approach_client: string,
  contract_validity_from: string,
  contract_validity_to: string,
  self_renewable: boolean,
  next_maint_date: string | null
}

type StafferType = {
  id: number,
  username: string
}

type AgencyType = {
  id: number,
  name: string,
  parks?: Array<number>
}

export const useMapStore = defineStore(
  'mapstore',
  () => {
    const show_preemptive = ref(true);
    const show_issues = ref(true);
    const map_park_list: Ref<Array<ParkType>> = ref([]);
    const staffers: Ref<Array<StafferType>> = ref([]);
    const agency_list: Ref<Array<AgencyType>> = ref([]);
    const ticket_list: Ref<Array<IssueType>> = ref([]);
    const loading_data: Ref<boolean> = ref(false);
    const parks_with_issues: Ref<Array<ParkType>> = ref([]);
    const agencies_with_issues: Ref<Array<AgencyType>> = ref([]);
    const filtered_prio: Ref<Array<number>> = ref([]);
    const filtered_agencies: Ref<Array<number>> = ref([]);
    const filtered_preemptives: Ref<Array<string>> = ref([
      get_preemptive_color("Red"),
      get_preemptive_color("Orange"),
      get_preemptive_color("Green")
    ]);
    const filtered_parks: Ref<Array<number>> = ref([]);
    const filtered_creators: Ref<Array<number>> = ref([]);
    const filtered_solved: Ref<Array<number>> = ref([]);
    const creators_with_issues = computed(() =>
      staffers.value.filter(s => ticket_list.value.map(t => t['made_by']).includes(s.id))
    );

    const priorities: Array<{ value: number, text: 'High' | 'Medium' | 'Low', color: string }> = [
      { value: 1, text: 'High', color: 'red' },
      { value: 2, text: 'Medium', color: 'orange' },
      { value: 3, text: 'Low', color: 'green' }
    ] as const;

    async function load_staffers() {
      loading_data.value = true;
      try {
        const response = await axios.get(
          endpoint('internal/current/users/?is_staff=1&is_active=1'),
          { withCredentials: true });
        if (response.statusText === "OK") {
          staffers.value = response.data;
        }
      } catch (e: any) {
        console.log(e);
      } finally {
        loading_data.value = false;
      }
    }

    async function load_parks() {
      loading_data.value = true;
      try {
        const map_response = await axios.get(
          endpoint('internal/current/map/parks/'),
          { withCredentials: true });
        if (map_response.statusText === "OK") {
          map_park_list.value = map_response.data.filter(p => p.installation_date !== null).map(p => {
            const has_contract = p.contract_info !== null && p.contract_info.contract && p.contract_info.contract_signed && !p.contract_info.contract_interrupted && p.contract_info.maintenance_amount_cent > 0;
            return {
              id: p.id,
              name: p.name,
              latitude: p.latitude,
              longitude: p.longitude,
              address: p.address,
              city: p.city,
              agency: p.agency,
              installation_date: p.installation_date,
              latest_preventive_maintenance_date: p.latest_preventive_maintenance_date,
              contract_maintenance_amount: p.contract_info !== null ? p.contract_info.maintenance_amount_cent / 100 : 0,
              has_contract: has_contract,
              contract_type: has_contract ? p.contract_info.contract_type : null,
              cabinet_position: p.electrical_cabinet_where,
              cabinet_key: p.electrical_cabinet_with_key,
              how_approach_client: p.how_approach_client,
              number_of_cs: p.number_of_cs,
              contract_validity_from: p.contract_info !== null ? p.contract_info.validity_from : null,
              contract_validity_to: p.contract_info !== null ? p.contract_info.validity_to : null,
              self_renewable: p.contract_info !== null ? p.contract_info.self_renewable : null,
              next_maint_date: null
            } as ParkType;
          });
          map_park_list.value = map_park_list.value.filter(p => p.has_contract);
          map_park_list.value = map_park_list.value.filter(p => p.contract_validity_from !== null);
          map_park_list.value.forEach(park => {
            const contractStartDate = new Date(park.contract_validity_from);
            const oneYearAfterContractStart = new Date(contractStartDate);
            oneYearAfterContractStart.setFullYear(oneYearAfterContractStart.getFullYear() + 1);

            if (park.latest_preventive_maintenance_date === null) {
              // No maintenance history: use one year after contract start
              park.next_maint_date = oneYearAfterContractStart.toISOString().split('T')[0];
            } else {
              const lastMaintenanceDate = new Date(park.latest_preventive_maintenance_date);
              // expected date is day and month of the contract start date
              const expectedMaintenanceDate = new Date(contractStartDate);
              expectedMaintenanceDate.setFullYear(lastMaintenanceDate.getFullYear());
              // With the first year available so that the date is AFTER the latest maintenance
              if (lastMaintenanceDate.getTime() - expectedMaintenanceDate.getTime() > 0) {
                expectedMaintenanceDate.setFullYear(lastMaintenanceDate.getFullYear() + 1);
              }

              // if lastMaint is too much nearby expected date, move the expected date to next year
              if (expectedMaintenanceDate.getTime() - lastMaintenanceDate.getTime() < 4 * monthMs) {
                expectedMaintenanceDate.setFullYear(expectedMaintenanceDate.getFullYear() + 1);
              }

              park.next_maint_date = expectedMaintenanceDate.toISOString().split('T')[0];
            }
          });
          // tengo tutti i parchi la cui prossima manutenzione cade prima della scadenza del contratto (+1 mese)
          map_park_list.value = map_park_list.value.filter(p => {
            const exp_date = Date.parse(p.contract_validity_to);
            return (Date.parse(p.next_maint_date!) < exp_date + monthMs + (p.self_renewable ? yearMs : 0));
          });
        }
      } catch (e: any) {
        console.log(e);
      } finally {
        loading_data.value = false;
      }
    }

    async function load_agencies() {
      loading_data.value = true;
      try {
        const response = await axios.get(
          endpoint('internal/current/agency/'),
          { withCredentials: true });
        if (response.statusText === "OK") {
          agency_list.value = response.data;
        }
      } catch (e: any) {
        console.log(e);
      } finally {
        loading_data.value = false;
      }
    }

    async function load_tickets() {
      loading_data.value = true;
      try {
        let next = true;
        let out: Array<any> = [];
        let url = endpoint('internal/current/tickets/?is_solved=0');
        while (next) {
          const response = await axios.get(url, { withCredentials: true });
          if (response.statusText === "OK") {
            out.push(...response.data.results);
            url = response.data.next;
            if (url === null || url === undefined) {
              next = false;
            }
          } else {
            next = false;
          }
        }
        // rimuovo da subito i ticket che non hanno una descrizione
        out = out.filter(t => (t['how_to_solve_descr'] as string).trim().length > 0);
        const park_ids = new Set();
        out.forEach(ticket => park_ids.add(ticket['park']));
        const park_requests = Array.from(park_ids).map(
          id => axios.get(endpoint(`internal/current/parks/${id}/`), { withCredentials: true })
        );
        const park_responses = await Promise.all(park_requests);
        for (const res of park_responses) {
          if (res.statusText !== "OK") {
            throw new Error(`Error loading park data: ${res.statusText}`);
          }
        }
        parks_with_issues.value = park_responses.map(res => res.data);
        parks_with_issues.value.forEach(park => {
          if (park.agency === null) {
            park.agency = -1;
          }
        });
        const agencies_ids = parks_with_issues.value.map(park => park['agency']);
        agencies_with_issues.value = agency_list.value.filter(agency => agencies_ids.includes(agency.id));
        agencies_with_issues.value.push({ id: -1, name: 'Not assigned' });
        agencies_with_issues.value.forEach((agency: AgencyType) => agency['parks'] = []);
        parks_with_issues.value.forEach(park => {
          for (const agency of agencies_with_issues.value) {
            if (park['agency'] === agency.id) {
              agency['parks']!.push(park['id']);
              break;
            }
          }
        });
        out.forEach((ticket: IssueType) =>
          ticket['agency'] = agencies_with_issues.value.find((a: AgencyType) => a['parks']!.includes(ticket['park']))
        );

        ticket_list.value = out;
      } catch (e: any) {
        console.log(e);
        throw e;
      } finally {
        loading_data.value = false;
      }
    }

    async function load_data() {
      loading_data.value = true;
      await load_parks();
      await load_agencies();
      await load_staffers();
      await load_tickets();
      loading_data.value = false;
    }

    const displayed_tickets = computed(() => {
      let out = ticket_list.value;
      out = filtered_agencies['value'].length > 0 ? out.filter(t => filtered_agencies['value'].includes(t.agency?.id ?? -1)) : out;
      out = filtered_creators['value'].length > 0 ? out.filter(t => filtered_creators['value'].includes(t.made_by)) : out;
      out = filtered_solved['value'].length > 0 ? out.filter(t => filtered_solved['value'].includes(t.is_solved ? 1 : 0)) : out;
      out = filtered_prio['value'].length > 0 ? out.filter(t => filtered_prio['value'].includes(t.priority)) : out;
      return out;
    });

    const markers_issues = computed(() => {
      return displayed_tickets.value.map((ticket: IssueType) => {
        const park = parks_with_issues.value.find(p => p.id === ticket.park);
        const agency = agencies_with_issues.value.find(a => a.id === park?.agency);
        return {
          name: park?.name,
          park: park?.id,
          address: park?.address + ', ' + park?.city,
          agency_id: agency?.id,
          date: ticket.start_date,
          agency: agency?.name ?? 'Not Assigned',
          location: { lat: parseFloat(park?.latitude ?? '0'), lng: parseFloat(park?.longitude ?? '0') },
          issue_id: ticket.id,
          issue_title: ticket.title,
          made_by: ticket.made_by,
          priority: ticket.priority,
          cabinet_key: park?.cabinet_key,
          cabinet_position: park?.cabinet_position,
          kind: MaintenanceKind.issue,
          issue_how_to_solve: ticket.how_to_solve_descr,
        } as IssueMarker;
      });
    });

    const markers_maintenances: Ref<Array<PreemptiveMaintenanceMarker>> = computed(() => {
      return map_park_list.value.filter(p => p.contract_maintenance_amount > 0).map(p => {
        const agency = agency_list.value.find(a => a.id === p?.agency);
        return {
          name: p?.name,
          park: p?.id,
          agency_id: agency?.id,
          address: p?.address + ', ' + p?.city,
          date: p.next_maint_date,
          agency: agency?.name ?? 'Not Assigned',
          location: { lat: parseFloat(p?.latitude ?? '0'), lng: parseFloat(p?.longitude ?? '0') },
          maintenance_subscription: p.contract_maintenance_amount,
          contract_type: p.contract_type,
          cabinet_key: p?.cabinet_key,
          cabinet_position: p?.cabinet_position,
          kind: MaintenanceKind.preemptive,
          how_approach_client: p?.how_approach_client,
          number_of_cs: p?.number_of_cs,
          last_preventive_date: p?.latest_preventive_maintenance_date
        } as PreemptiveMaintenanceMarker;
      }).toSorted(compare_markers);
    });

    const sorted_issues = computed(() => {
      let out: Array<IssueMarker> = [];
      if (show_issues.value) {
        out = markers_issues.value.toSorted(compare_issues);
        out = filtered_parks['value'].length > 0 ? out.filter(m => filtered_parks['value'].includes(m.park)) : out;
      }
      return out;
    });

    const sorted_preemptive = computed(() => {
      let out: Array<PreemptiveMaintenanceMarker> = [];
      if (show_preemptive.value) {
        out = markers_maintenances.value;
        out = filtered_parks['value'].length > 0 ? out.filter(m => filtered_parks['value'].includes(m.park)) : out;
        out = filtered_agencies['value'].length > 0 ? out.filter(t => filtered_agencies['value'].includes(t.agency_id ?? -1)) : out;
        out = filtered_preemptives['value'].length > 0 ? out.filter(m => filtered_preemptives['value'].includes(maintenance_marker_color(m))) : out;
        return out;
      } else {
        return out;
      }
    });

    const maintenance_park_names = computed(() => {
      let maint_out = markers_maintenances.value.map(m => {
        return {
          "id": m.park,
          "name": m.name
        };
      });
      let issue_out = markers_issues.value.map(m => {
        return {
          "id": m.park,
          "name": m.name
        };
      });
      return [...maint_out, ...issue_out];
    });

    const all_markers: Ref<Array<PreemptiveMaintenanceMarker | IssueMarker>> = computed(() =>
      [...sorted_preemptive.value, ...sorted_issues.value]
    );

    return {
      staffers, agency_list, ticket_list, loading_data, priorities, preemptive_colors, markers_issues,
      displayed_tickets, parks_with_issues, agencies_with_issues, creators_with_issues, markers_maintenances,
      filtered_agencies, filtered_parks, filtered_creators, filtered_solved, filtered_prio, filtered_preemptives,
      show_issues, show_preemptive, sorted_issues, sorted_preemptive, all_markers, maintenance_park_names,
      load_data
    };
  }
);
